import type { ReactElement } from "react";

/**
 * Sidebar section icons.
 *
 * Local to staff-ui on purpose: the design system's Icon set is 41 generic
 * icons, not enough to tell fourteen sections apart without forcing bad
 * matches. These are drawn on a shared 24x24 grid with a single stroke weight
 * so the collapsed rail reads as one set.
 */
export type SidebarIconName =
  | "home"
  | "submissions"
  | "research"
  | "surveys"
  | "profiles"
  | "cms"
  | "marketing"
  | "awards"
  | "taxonomy"
  | "addressBook"
  | "dataMarts"
  | "fulfilment"
  | "people"
  | "sync"
  | "operations"
  | "maintenance"
  | "events"
  | "powerlist"
  | "star";

const PATHS: Record<SidebarIconName, ReactElement> = {
  // House
  home: (
    <>
      <path d="M3.5 10.5 12 3.75l8.5 6.75" />
      <path d="M5.75 9.5v10.75h12.5V9.5" />
    </>
  ),
  // Inbox tray
  submissions: (
    <>
      <path d="M4 13.5h4l1.5 2.5h5l1.5-2.5h4" />
      <path d="M6.6 4.75h10.8L20 13.5v4.75a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.25V13.5z" />
    </>
  ),
  // Beaker
  research: (
    <>
      <path d="M9.5 3.75v6.1L5.1 17.4a1.9 1.9 0 0 0 1.6 2.85h10.6a1.9 1.9 0 0 0 1.6-2.85L14.5 9.85V3.75" />
      <path d="M8.25 3.75h7.5" />
      <path d="M7.4 14.5h9.2" />
    </>
  ),
  // Clipboard with a tick
  surveys: (
    <>
      <path d="M9.25 4.5h5.5v2.75h-5.5z" />
      <path d="M15.75 5.75h1.75a1.5 1.5 0 0 1 1.5 1.5v11.5a1.5 1.5 0 0 1-1.5 1.5h-11a1.5 1.5 0 0 1-1.5-1.5V7.25a1.5 1.5 0 0 1 1.5-1.5h1.75" />
      <path d="m9.25 13.5 2 2 3.5-3.75" />
    </>
  ),
  // Profile card
  profiles: (
    <>
      <path d="M3.75 5.25h16.5v13.5H3.75z" />
      <path d="M8.75 11.25a1.85 1.85 0 1 0 0-3.7 1.85 1.85 0 0 0 0 3.7z" />
      <path d="M5.9 16c.55-1.75 1.75-2.6 2.85-2.6s2.3.85 2.85 2.6" />
      <path d="M14.5 9.75h3.5M14.5 13.25h3.5" />
    </>
  ),
  // Browser window
  cms: (
    <>
      <path d="M3.75 5.25h16.5v13.5H3.75z" />
      <path d="M3.75 9.25h16.5" />
      <path d="M6.5 7.25h.01M9 7.25h.01" />
    </>
  ),
  // Megaphone
  marketing: (
    <>
      <path d="M4 10.25v3.5a1.25 1.25 0 0 0 1.25 1.25H7.5l6 4V5l-6 4H5.25A1.25 1.25 0 0 0 4 10.25z" />
      <path d="M17 9.25a4 4 0 0 1 0 5.5" />
      <path d="M7.5 15v4.25" />
    </>
  ),
  // Trophy
  awards: (
    <>
      <path d="M8 4.25h8v3.5a4 4 0 0 1-8 0z" />
      <path d="M8 5.5H5.25v1.25a3 3 0 0 0 3 3M16 5.5h2.75v1.25a3 3 0 0 1-3 3" />
      <path d="M12 11.75v4.5" />
      <path d="M8.75 20.25h6.5l-.6-4h-5.3z" />
    </>
  ),
  // Layers
  taxonomy: (
    <>
      <path d="m12 3.75 8.25 4.5L12 12.75 3.75 8.25z" />
      <path d="m3.75 12.75 8.25 4.5 8.25-4.5" />
      <path d="m3.75 17 8.25 4.5L20.25 17" />
    </>
  ),
  // Address book
  addressBook: (
    <>
      <path d="M7 3.75h11.25a1 1 0 0 1 1 1v14.5a1 1 0 0 1-1 1H7a1.75 1.75 0 0 1-1.75-1.75V5.5A1.75 1.75 0 0 1 7 3.75z" />
      <path d="M5.25 8.25H3.5M5.25 12H3.5M5.25 15.75H3.5" />
      <path d="M12.25 11.5a1.85 1.85 0 1 0 0-3.7 1.85 1.85 0 0 0 0 3.7z" />
      <path d="M9.4 16.25c.55-1.75 1.75-2.6 2.85-2.6s2.3.85 2.85 2.6" />
    </>
  ),
  // Database
  dataMarts: (
    <>
      <path d="M12 3.75c4.3 0 7.75 1.1 7.75 2.5S16.3 8.75 12 8.75 4.25 7.65 4.25 6.25 7.7 3.75 12 3.75z" />
      <path d="M4.25 6.25v11.5c0 1.4 3.45 2.5 7.75 2.5s7.75-1.1 7.75-2.5V6.25" />
      <path d="M4.25 12c0 1.4 3.45 2.5 7.75 2.5s7.75-1.1 7.75-2.5" />
    </>
  ),
  // Package
  fulfilment: (
    <>
      <path d="m12 3.5 8 4.4v8.2l-8 4.4-8-4.4V7.9z" />
      <path d="m4 7.9 8 4.4 8-4.4" />
      <path d="M12 12.3v8.2" />
    </>
  ),
  // Two people
  people: (
    <>
      <path d="M9.25 11.5a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5z" />
      <path d="M3.25 20c0-3.2 2.7-5.25 6-5.25s6 2.05 6 5.25" />
      <path d="M16 5.4a3.25 3.25 0 0 1 0 6.2" />
      <path d="M17.4 15.1c2.05.7 3.35 2.4 3.35 4.9" />
    </>
  ),
  // Refresh arrows
  sync: (
    <>
      <path d="M19.75 12a7.75 7.75 0 0 1-13.3 5.4" />
      <path d="M4.25 12a7.75 7.75 0 0 1 13.3-5.4" />
      <path d="M17.75 3.25v3.5h-3.5M6.25 20.75v-3.5h3.5" />
    </>
  ),
  // Sliders
  operations: (
    <>
      <path d="M4 7h9M17.5 7h2.5M4 12h3M11.5 12h8.5M4 17h9M17.5 17h2.5" />
      <path d="M15.25 8.75a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5z" />
      <path d="M9.25 13.75a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5z" />
      <path d="M15.25 18.75a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5z" />
    </>
  ),
  // Calendar
  events: (
    <>
      <path d="M4.25 6.5h15.5v13.25H4.25z" />
      <path d="M4.25 10.5h15.5" />
      <path d="M8.5 4.25v4M15.5 4.25v4" />
    </>
  ),
  powerlist: (
    <>
      <path d="M4.25 6.5h1.5v-2.25" />
      <path d="M4 13h2.25L4 16h2.25" />
      <path d="M9.75 5.75h10M9.75 12h10M9.75 18.25h10" />
    </>
  ),
  // Star — fill is driven by CSS so one path serves both states.
  star: (
    <path d="m12 4 2.55 5.17 5.7.83-4.13 4.02.98 5.68L12 17.02l-5.1 2.68.98-5.68L3.75 10l5.7-.83z" />
  ),
  // Spanner
  maintenance: (
    <>
      <path d="M15.4 4.4a4.6 4.6 0 0 0-6 6l-5.15 5.15v4.05h4.05L13.45 14.5a4.6 4.6 0 0 0 6-6l-2.85 2.85-2.25-2.25z" />
    </>
  ),
};

type SidebarIconProps = {
  name: SidebarIconName;
  className?: string;
};

export function SidebarIcon({ name, className }: SidebarIconProps): ReactElement {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
